import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CreateTransactionData } from "@/types/bank-statement";
import { TRANSACTION_CATEGORIES } from "../../constants/transaction-categories";
import { ChevronDown } from "lucide-react";

interface LaunchOperationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: Partial<CreateTransactionData>) => void;
  isSaving?: boolean;
}

export function LaunchOperationModal({ isOpen, onClose, onSave, isSaving }: LaunchOperationModalProps) {
  const [transactionDate, setTransactionDate] = useState("");
  const [description, setDescription] = useState("");
  const [transactionType, setTransactionType] = useState<"CREDIT" | "DEBIT" | "">("");
  const [amount, setAmount] = useState<string>("");
  const [category, setCategory] = useState("");

  useEffect(() => {
    if (isOpen) {
      setTransactionDate("");
      setDescription("");
      setTransactionType("");
      setAmount("");
      setCategory("");
    }
  }, [isOpen]);

  const handleSave = () => {
    if (!transactionDate || !description || !transactionType || !amount || !category) return;

    onSave({
      transactionDate,
      description,
      transactionType: transactionType as "CREDIT" | "DEBIT",
      amount: parseFloat(amount),
      category,
    });
  };

  const isFormValid = transactionDate && description && transactionType && amount && category;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[600px] bg-[#0c1427] border-[#424242] text-white p-8">
        <DialogHeader className="mb-4">
          <DialogTitle className="text-2xl font-bold font-['Plus_Jakarta_Sans']">Lançar  Operação</DialogTitle>
        </DialogHeader>
        
        <div className="flex flex-col gap-6 py-2">
          <div className="flex flex-col gap-2">
            <label className="text-white text-base font-medium font-['Plus_Jakarta_Sans']">Data do Pagamento <span className="text-red-500">*</span></label>
            <Input
              type="date"
              value={transactionDate}
              onChange={(e) => setTransactionDate(e.target.value)}
              className="text-lg h-[54px] px-4"
            />
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-white text-base font-medium font-['Plus_Jakarta_Sans']">Descrição <span className="text-red-500">*</span></label>
            <Input
              type="text"
              placeholder="Ex: Pagamento Fornecedor"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="text-lg h-[54px] px-4"
            />
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-white text-base font-medium font-['Plus_Jakarta_Sans']">Tipo da Transação <span className="text-red-500">*</span></label>
            <div className="relative">
              <select
                value={transactionType}
                onChange={(e) => setTransactionType(e.target.value as "CREDIT" | "DEBIT")}
                className="flex items-center w-full h-[54px] px-[20px] rounded-lg bg-[#15203c] text-white outline-none border-[0.5px] border-[#5d5d5d] focus:ring-1 focus:ring-[#7846ff] focus:ring-offset-0 text-lg placeholder:text-[#d1d1d1] appearance-none pr-12 cursor-pointer"
              >
                <option value="" disabled className="bg-[#15203c] text-white">Selecione um tipo...</option>
                <option value="CREDIT" className="bg-[#15203c] text-white">Entrada (Crédito)</option>
                <option value="DEBIT" className="bg-[#15203c] text-white">Saída (Débito)</option>
              </select>
              <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-6 h-6 text-[#d1d1d1] pointer-events-none" />
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-white text-base font-medium font-['Plus_Jakarta_Sans']">Valor (R$) <span className="text-red-500">*</span></label>
            <Input
              type="number"
              step="0.01"
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="text-lg h-[54px] px-4"
            />
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-white text-base font-medium font-['Plus_Jakarta_Sans']">Categoria da Operação <span className="text-red-500">*</span></label>
            <div className="relative">
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="flex items-center w-full h-[54px] px-[20px] rounded-lg bg-[#15203c] text-white outline-none border-[0.5px] border-[#5d5d5d] focus:ring-1 focus:ring-[#7846ff] focus:ring-offset-0 text-lg placeholder:text-[#d1d1d1] appearance-none pr-12 cursor-pointer"
              >
                <option value="" disabled className="bg-[#15203c] text-white">Selecione uma categoria...</option>
                {TRANSACTION_CATEGORIES.map((cat) => (
                  <option key={cat} value={cat} className="bg-[#15203c] text-white">
                    {cat}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-6 h-6 text-[#d1d1d1] pointer-events-none" />
            </div>
          </div>
        </div>

        <DialogFooter className="gap-4 sm:gap-4 mt-6">
          <Button onClick={onClose} variant="outline" className="border-[#424242] text-white hover:bg-white/10 h-[48px] px-6 text-lg" disabled={isSaving}>
            Cancelar
          </Button>
          <Button 
            onClick={handleSave} 
            className="bg-[#7846ff] hover:bg-[#6035d8] text-white h-[48px] px-8 text-lg font-bold"
            disabled={!isFormValid || isSaving}
          >
            {isSaving ? "Salvando..." : "Salvar Lançamento"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
